import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  CreditCard as RefundIcon
} from '@mui/icons-material';
import moment from 'moment';

const BookingCard = ({ booking, onAction }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'hold': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmado';
      case 'hold': return 'Reservado';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      completed: 'Pago',
      failed: 'Falhou',
      refunded: 'Reembolsado'
    };
    return labels[status] || status;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      completed: 'success',
      failed: 'error',
      refunded: 'default'
    };
    return colors[status] || 'default';
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount || 0);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action) => {
    onAction(action, booking);
    handleMenuClose();
  };

  const paymentStatus = booking.payment?.status || 'pending';

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1, '&:last-child': { pb: 2 } }}>
        {/* Header: Date/Time + Actions */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {moment(booking.startAt).format('ddd, D/MMM')}
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {moment(booking.startAt).format('HH:mm')}
            </Typography>
          </Box>
          {booking.status !== 'cancelled' && (
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ mt: -1, mr: -1 }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Main info grid */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Serviço</Typography>
            <Typography variant="body2" fontWeight={500}>
              {booking.service?.name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Valor</Typography>
            <Typography variant="body2" fontWeight={600} color="primary">
              {formatPrice(booking.payment?.amount)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Cliente</Typography>
            <Typography variant="body2">
              {booking.customer?.name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Barbeiro</Typography>
            <Typography variant="body2">
              {booking.employee?.name || 'Sem preferência'}
            </Typography>
          </Grid>
        </Grid>

        {/* Status chips */}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Chip
            label={getStatusLabel(booking.status)}
            color={getStatusColor(booking.status)}
            size="small"
          />
          <Chip
            label={getPaymentStatusLabel(paymentStatus)}
            color={getPaymentStatusColor(paymentStatus)}
            size="small"
          />
        </Stack>

        {/* Footer: Created at */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Agendado {moment(booking.createdAt).fromNow()}
        </Typography>
      </CardContent>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {booking.status === 'hold' && (
          <MenuItem onClick={() => handleMenuAction('confirm')}>
            <CheckIcon sx={{ mr: 1, fontSize: 20 }} /> Confirmar
          </MenuItem>
        )}
        {booking.status === 'hold' && <Divider />}
        {booking.status !== 'cancelled' && (
          <MenuItem onClick={() => handleMenuAction('cancel')} sx={{ color: 'error.main' }}>
            <CancelIcon sx={{ mr: 1, fontSize: 20 }} /> Cancelar
          </MenuItem>
        )}
        {paymentStatus === 'completed' && booking.status !== 'cancelled' && (
          <MenuItem onClick={() => handleMenuAction('refund')} sx={{ color: 'warning.main' }}>
            <RefundIcon sx={{ mr: 1, fontSize: 20 }} /> Reembolsar
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default BookingCard;
